import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, openBlock as _openBlock, toDisplayString as _toDisplayString, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, vShow as _vShow, withDirectives as _withDirectives, resolveDirective as _resolveDirective, createBlock as _createBlock, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = (n: any) => (_pushScopeId("data-v-3e01efe6"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "alert-trend-card__header-actions" }
const _hoisted_2 = {
  class: "cm-align-center",
  style: {"position":"relative","top":"-4px"}
}
const _hoisted_3 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("svg", {
  t: "1776156941833",
  class: "icon",
  viewBox: "0 0 1024 1024",
  version: "1.1",
  xmlns: "http://www.w3.org/2000/svg",
  "p-id": "6286",
  width: "18",
  height: "18"
}, [
  /*#__PURE__*/_createElementVNode("path", {
    d: "M155.04 448v-64h567.392l-186.464-194.24 43.616-45.44 291.2 303.68zM149.504 576H865.28v64H297.6l186.432 194.24-43.296 45.44z",
    fill: "var(--el-color-primary)",
    "p-id": "6287"
  })
], -1))
const _hoisted_4 = { style: {"margin-left":"4px","font-size":"14px"} }
const _hoisted_5 = { class: "chart-container" }

import { ref, computed, onUnmounted, watch, nextTick } from 'vue'
import type { TimeRange } from '@/views/dashboard/_components/time-range-select/time-range.enum'
import { useI18n } from 'vue-i18n'
import { useReflectiveInjector } from '@tanbo/vue-di-plugin'
import * as echarts from 'echarts'
import { ResizeService } from '@/services/resize.service'
import { getAssignLineOption, withLineSeriesStyle } from '@/utils/echarts-option/line'
import { useEchartsThemeSync } from '@/composables/use-echarts-theme-sync'
import Card from '@/views/dashboard/_components/card/card.vue'
import EmptyData from '@/views/dashboard/_components/empty-data/empty-data.vue'
import { RealtimeMonitoringService, AlertTrendData, AlertTrendResponse } from './realtime-monitoring.service'
import { ALERT_TREND_SERIES_COLORS } from './alert-trend-series-colors'
import TimeRangeSelect from '@/views/dashboard/_components/time-range-select/time-range-select.vue'


export default /*#__PURE__*/_defineComponent({
  __name: 'alert-trend-card',
  props: {
  timeRange: {
    type: String,
    default: '30d'
  },
  /** 与 end_time 同时传入时优先于按 timeRange 推算的区间 */
  start_time: {
    type: String,
    default: undefined
  },
  end_time: {
    type: String,
    default: undefined
  }
},
  emits: ["update:timeRange"],
  setup(__props, { emit: __emit }) {

const props = __props

const emit = __emit

const timeRangeModel = computed({
  get: () => props.timeRange,
  set: (v: TimeRange | string) => emit('update:timeRange', v)
})

const { t, locale } = useI18n()

const injector = useReflectiveInjector([RealtimeMonitoringService, ResizeService])
const service = injector.get(RealtimeMonitoringService)
const resizeService = injector.get(ResizeService)

const isLoading = ref(false)
const rawData = ref<AlertTrendData[]>([])

/** 默认风险视图；终端视图：`/terminal/risk-trend`；风险视图：`/terminal/risk-event-trend` */
const trendViewMode = ref<'terminal' | 'risk'>('risk')

const toggleButtonText = computed(() => {
  void locale.value
  return trendViewMode.value === 'risk'
    ? t('overviewZone.alertTrendShowTerminalView')
    : t('overviewZone.alertTrendShowRiskView')
})

function toggleTrendView () {
  trendViewMode.value = trendViewMode.value === 'risk' ? 'terminal' : 'risk'
  loadData()
}

function loadData () {
  isLoading.value = true
  const params = {
    time_range: props.timeRange,
    start_time: props.start_time,
    end_time: props.end_time,
  }
  const req =
    trendViewMode.value === 'risk'
      ? service.getTerminalRiskEventTrend(params)
      : service.getAlertTrend(params)
  req
    .then((res: AlertTrendResponse) => {
      rawData.value = res.data || []
    })
    .catch(() => {
      rawData.value = []
    })
    .finally(() => {
      isLoading.value = false
    })
}

watch(
  () => [props.timeRange, props.start_time, props.end_time] as const,
  () => {
    loadData()
  },
  { immediate: true }
)

/** 切换风险/终端视图时接口未返回前也要更新图例文案 */
watch(trendViewMode, () => {
  if (!rawData.value.length || !chartInstance) return
  nextTick(() => renderChart())
})

/** 终端视图图例：沿用「发现xxx终端数」；风险视图图例：仅风险类型简称 */
const SERIES_FIELDS = [
  { field: 'ransomware_risk' as const, nameKeyTerminal: 'overviewZone.alertSeriesRansomware', nameKeyRisk: 'overviewZone.alertTrendLegendRiskRansomware', color: ALERT_TREND_SERIES_COLORS.ransomware_risk },
  { field: 'virus_risk' as const, nameKeyTerminal: 'overviewZone.alertSeriesVirus', nameKeyRisk: 'overviewZone.alertTrendLegendRiskVirus', color: ALERT_TREND_SERIES_COLORS.virus_risk },
  { field: 'vulnerability_risk' as const, nameKeyTerminal: 'overviewZone.alertSeriesVulnerability', nameKeyRisk: 'overviewZone.alertTrendLegendRiskVulnerability', color: ALERT_TREND_SERIES_COLORS.vulnerability_risk },
  { field: 'baseline_violation' as const, nameKeyTerminal: 'overviewZone.alertSeriesBaseline', nameKeyRisk: 'overviewZone.alertTrendLegendRiskBaseline', color: ALERT_TREND_SERIES_COLORS.baseline_violation },
  { field: 'port_risk' as const, nameKeyTerminal: 'overviewZone.alertSeriesPort', nameKeyRisk: 'overviewZone.alertTrendLegendRiskPort', color: ALERT_TREND_SERIES_COLORS.port_risk },
] as const

const chartRef = ref<HTMLElement>()
let chartInstance: echarts.EChartsType | null = null

const chartOption = computed(() => {
  void locale.value
  void trendViewMode.value
  if (!rawData.value.length) {
    return { xAxis: { data: [] }, series: [] }
  }

  const times = rawData.value.map(item => item.time)
  const nameKeyProp = trendViewMode.value === 'risk' ? 'nameKeyRisk' : 'nameKeyTerminal'
  return {
    xAxis: {
      data: times,
    },
    legend: {
      width: chartInstance ? chartInstance.getWidth() - 60 : 0,
    },
    series: SERIES_FIELDS.map(config => withLineSeriesStyle({
      name: t(config[nameKeyProp]),
      data: rawData.value.map(item => item[config.field as keyof AlertTrendData]),
      smooth: true,
    }, config.color))
  }
})

function initChart () {
  if (!chartRef.value) return
  chartInstance = echarts.init(chartRef.value)
  renderChart()
}

function renderChart () {
  if (!chartInstance) return
  const finalOption: any = getAssignLineOption(chartOption.value, {})
  chartInstance.setOption(finalOption)
}

const resizeSubscribe = resizeService.resizeSubject.subscribe(() => {
  chartInstance && chartInstance.resize()
})

watch(() => rawData.value, () => {
  if (!rawData.value.length) return
  if (!chartInstance) {
    nextTick(initChart)
  } else {
    renderChart()
  }
}, { deep: true })

watch(locale, () => {
  if (!rawData.value.length || !chartInstance) return
  nextTick(() => renderChart())
})

useEchartsThemeSync(renderChart)

onUnmounted(() => {
  resizeSubscribe.unsubscribe()
  chartInstance && chartInstance.dispose()
})

return (_ctx: any,_cache: any) => {
  const _component_el_button = _resolveComponent("el-button")!
  const _directive_loading = _resolveDirective("loading")!

  return _withDirectives((_openBlock(), _createBlock(Card, {
    size: "second",
    title: _ctx.$t('overviewZone.alertTrendTitle'),
    class: "alert-trend-card-wrap"
  }, {
    headerRight: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        _createVNode(_component_el_button, {
          type: "primary",
          size: "mini",
          plain: "",
          class: "cm-align-center",
          style: {"height":"28px"},
          onClick: toggleTrendView
        }, {
          default: _withCtx(() => [
            _createElementVNode("div", _hoisted_2, [
              _hoisted_3,
              _createElementVNode("span", _hoisted_4, _toDisplayString(toggleButtonText.value), 1)
            ])
          ]),
          _: 1
        }),
        _createVNode(TimeRangeSelect, {
          modelValue: timeRangeModel.value,
          "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((timeRangeModel).value = $event))
        }, null, 8, ["modelValue"])
      ])
    ]),
    default: _withCtx(() => [
      _withDirectives(_createElementVNode("div", _hoisted_5, [
        _createElementVNode("div", {
          ref_key: "chartRef",
          ref: chartRef,
          class: "chart-wrapper"
        }, null, 512)
      ], 512), [
        [_vShow, rawData.value?.length]
      ]),
      _withDirectives(_createVNode(EmptyData, {
        height: "292px",
        "bg-color": "transparent"
      }, null, 512), [
        [_vShow, !rawData.value?.length]
      ])
    ]),
    _: 1
  }, 8, ["title"])), [
    [_directive_loading, isLoading.value]
  ])
}
}

})